import React, {useEffect, useState} from 'react';
import {Select, Input, InputNumber, TimePicker, Form, Button, Space, Radio, Checkbox, Alert} from 'antd';
import {internalApi} from "../../common/axios";
import {
    DIALOG_KEY,
    OPERATION_TYPE_TRIGGER,
    onOffList,
    operationTypeList,
    triggerActionList,
    triggerOpList,
    triggerTypeList,
    weekList,
    weekdayPresetList
} from "../../shared/utils/enum";
import {buildTriggerSteps} from "../../shared/utils/triggerEnum";
import CustomDateTimePicker from "../../shared/components/date/CustomDateTimePicker";
import toast from "react-hot-toast";
import duration from 'dayjs/plugin/duration';
import dayjs from "dayjs";
import {formatMinutes, fromUnixTimeFormat, getTotalMinutes} from "../../shared/utils/dayjsUtil";
import {buildUrl} from "../../shared/utils/net";
import {EventEntity} from "./EventEntity";
import {CancelButton, DeleteButton, SaveButton} from "../../shared/components/buttons/CustomButtons";
import {useEventSchedule, useEventTable} from "./useEventTable";
import IsLoading from "../../shared/components/IsLoading";
import ErrorPage from "../../shared/components/ErrorPage";
import DateStepper from "../../shared/components/date/DateStepper";
import {useEventManager} from "./useEventManager";

const {Option} = Select;

dayjs.extend(duration);

// segmentList 를 넘기면 세그먼트 이벤트(event_segment_master) 편집 모드
const EventEditModal = ({record, handleClose, dialogRef, handleSetDialog, segmentList}) => {
    const apiPath = segmentList ? 'events/segment' : 'events';
    const initTypeIndex = record.typeIndex || 10101;
    const {data: eventTable, isLoading, error} = useEventTable();
    const {data: eventList, isLoading: isEventListLoading, error: eventListError} = useEventManager();
    const {data: eventSchedule, isLoading: isScheduleLoading} = useEventSchedule();
    const [rows, setRows] = useState(record.seq ? new EventEntity(record) : new EventEntity({...eventTable[initTypeIndex], typeIndex: initTypeIndex, segmentIdx: record.segmentIdx, memo: eventList?.find((el) => el.eventId === Number(initTypeIndex))?.memo || ""}));
    const [isSurprise, setIsSurprise] = useState(eventTable[rows.typeIndex]?.isSurpriseEvent || 0);

    const minArrived = 1;
    const isTrigger = rows.operationType === OPERATION_TYPE_TRIGGER;

    const handleSetRows = (key, value) => {
        if (!eventList) return;
        if (key === 'typeIndex') {
            setRows(new EventEntity({
                ...eventTable[value],
                typeIndex: value,
                segmentIdx: rows.segmentIdx,
                memo: eventList.find((el) => el.eventId === Number(value))?.memo || "",
                surpriseTime: eventTable[value]?.isSurpriseEvent ? 60 : 0
            }));
            setIsSurprise(eventTable[value]?.isSurpriseEvent);
        } else {
            setRows((prevState) => {
                const update = {
                    ...prevState,
                    [key]: value
                };
                return new EventEntity(update);
            });
        }
    };

    const setTrigger = (i, patch) => handleSetRows('triggers', rows.triggers.map((t, idx) => idx === i ? {...t, ...patch} : t));
    // op = 앞 조건과의 결합(0:AND, 1:OR). 첫 조건에는 적용되지 않는다
    const addTrigger = (op) => handleSetRows('triggers', [...rows.triggers, {op, type: 1, targetId: null, count: 1}]);
    const removeTrigger = (i) => handleSetRows('triggers', rows.triggers.filter((_, idx) => idx !== i));

    if (isLoading || isEventListLoading || isScheduleLoading) return <IsLoading/>;
    const firstError = eventListError || error;
    if (firstError) return <ErrorPage error={error}/>;

    const triggerSteps = buildTriggerSteps(rows.triggers);

    const triggerLabel = (trigger) => {
        if (trigger.type === 1) {
            const name = eventTable[trigger.targetId]?.nameText || trigger.targetId;
            return trigger.targetId ? `${name} ${trigger.count}회 완료` : '(대상 미선택)';
        }
        const action = triggerActionList.find(a => a.value === trigger.targetId);
        return action ? `${action.label}${action.isState ? '' : ` ${trigger.count}`}` : '(대상 미선택)';
    };

    // 선행 조건으로 지정한 이벤트가 이 이벤트 기간에 편성돼 있지 않으면 등록할 수 없다.
    const missingTriggerEvents = !isTrigger ? [] : rows.triggers
        .filter(trigger => trigger.type === 1 && trigger.targetId)
        .filter(trigger => !(eventSchedule || []).some(row =>
            row.seq !== rows.seq
            && row.typeIndex === trigger.targetId
            && row.begin < rows.end
            && row.end > rows.begin))
        .map(trigger => eventTable[trigger.targetId]?.nameText || trigger.targetId);

    // 포인트 카니발(eventType 36)은 종속 이벤트 기간에 맞춰 운영한다.
    // carnivalEventType = 종속 이벤트의 eventType (csvLoader 가 Event_PointEventCarnival 을 보고 붙여준 값)
    const carnivalEventType = eventTable[rows.typeIndex]?.carnivalEventType || 0;
    // 종속 이벤트는 타입으로만 지정되므로 같은 타입의 아무 이벤트나 이름 표시에 쓴다.
    const eventTypeName = (eventType) => Object.values(eventTable).find(el => el.eventType === eventType)?.nameText || eventType;
    const periodText = (row) => `${fromUnixTimeFormat(row.begin)} ~ ${fromUnixTimeFormat(row.end)}`;

    // 아직 종료되지 않은 종속 이벤트 편성 (on/off 무관)
    const carnivalBaseEvents = !carnivalEventType ? [] : (eventSchedule || [])
        .filter(row => eventTable[row.typeIndex]?.eventType === carnivalEventType && row.end >= dayjs().unix())
        .sort((a, b) => a.begin - b.begin);

    // 카니발 기간은 종속 이벤트 한 건의 기간 안에 들어가야 한다 (서버 event.js 와 같은 기준)
    const carnivalOutOfRange = carnivalBaseEvents.length > 0
        && !carnivalBaseEvents.some(row => rows.begin >= row.begin && rows.end <= row.end);

    // 카니발은 참조 이벤트가 달라도 동시에 하나만 운영한다.
    // 경계가 맞닿는 것도 겹침으로 본다 (kitty_china_admin 과 같은 기준)
    const carnivalOverlaps = !carnivalEventType ? [] : (eventSchedule || [])
        .filter(row => row.seq !== rows.seq
            && eventTable[row.typeIndex]?.carnivalEventType > 0
            && row.begin <= rows.end
            && row.end >= rows.begin);

    const setWeekday = (day, checked) => handleSetRows('weekdays',
        checked ? rows.weekdays | (1 << day) : rows.weekdays & ~(1 << day));
    const weekdayPreset = weekdayPresetList.find(preset => preset.value === rows.weekdays)?.value ?? 0;

    const handleDelete = async (e) => {
        e.preventDefault();

        const currentDialog = dialogRef.current;  // ref에서 최신 상태 가져오기
        if (currentDialog.confirmText === 'DELETE') {
            await internalApi.delete(buildUrl(apiPath, {seq: rows.seq})).then((res) => {
                toast.success(res.data.message);
                handleClose(true);
            }).catch((err) => {
                toast.error("삭제 실패");
                console.error(err);
            });

            handleSetDialog([{key: DIALOG_KEY.OPEN, value: false}, {key: DIALOG_KEY.CONFIRM_TEXT, value: ""}]);
        } else {
            toast.error("잘못된 확인 문자입니다. DELETE 를 입력하세요.");
            handleSetDialog([{key: DIALOG_KEY.CONFIRM_TEXT, value: ''}]);
        }
    }

    const handleCheck = (e) => {
        e.preventDefault();
        if (rows.index === 0) {
            return internalApi.get(buildUrl('events/check', {
                eventIndex: rows.eventIndex,
                typeIndex: rows.typeIndex
            })).then((res) => {
                if (res.data.rows[0].count > 0) {
                    handleSetDialog([
                        {key: DIALOG_KEY.OPEN, value: true},
                        {key: DIALOG_KEY.TITLE, value: "저장 확인"},
                        {key: DIALOG_KEY.BODY, value: "이미 존재하는 이벤트가 존재합니다. \n그래도 저장 하시겠습니까?"},
                        {key: DIALOG_KEY.HANDLE, value: handleSave},
                        {key: DIALOG_KEY.BUTTON_TEXT, value: "저장"},
                        {key: DIALOG_KEY.USE_INPUT, value: false}
                    ])
                } else {
                    return handleSave(e);
                }
            }).catch((err) => {
                console.error(err);
                toast.error("이벤트 확인 실패");
            });
        } else {
            return handleSave(e);
        }
    }

    // rows가 EventEntity 인스턴스가 아닐 수 있으므로, handleSave에서 안전하게 처리
    const handleSave = async (e) => {
        e.preventDefault();

        // rows가 EventEntity 인스턴스가 아니면 새로 생성
        const entity = rows instanceof EventEntity ? rows : new EventEntity(rows);
        if (typeof entity.isInvalidate === 'function' && entity.isInvalidate()) return;

        await internalApi.post(apiPath, {id: entity.eventIndex, data: entity.toJson()}).then((res) => {
            toast.success(res.data.message);
            handleClose(true);
        }).catch((err) => {
            console.error(err);
        });
        handleSetDialog([{key: DIALOG_KEY.OPEN, value: false}, {key: DIALOG_KEY.CONFIRM_TEXT, value: ""}]);
    }

    return (
        <div className={"eventCreate"} style={{marginTop: 26}}>
            <Form layout="vertical">
                <div className={"param"} style={{display: 'flex', gap: 16, alignItems: 'flex-start'}}>
                    <Form.Item label="인덱스" style={{flex: 1, minWidth: 200}}>
                        <Select
                            value={rows.typeIndex}
                            onChange={value => handleSetRows('typeIndex', value)}
                            size="middle"
                            disabled={isEventListLoading || !eventList}
                            showSearch
                            filterOption={(input, option) => {
                                const searchValue = input.toLowerCase();
                                const eventId = option.value.toString();
                                const eventName = eventTable[option.value]?.nameText || '';
                                return eventId.includes(searchValue) || eventName.toLowerCase().includes(searchValue);
                            }}
                            dropdownRender={menu => (
                                <div>
                                    {menu}
                                    {(() => {
                                        if (!eventList) return null;
                                        const found = eventList.find(el => el.eventId === Number(rows.typeIndex));
                                        if (found?.memo) {
                                            return <div style={{
                                                padding: '8px 16px',
                                                color: '#888',
                                                fontStyle: 'italic',
                                                borderTop: '1px solid #eee'
                                            }}><b>메모:</b> {found.memo}</div>;
                                        }
                                        return null;
                                    })()}
                                </div>
                            )}
                        >
                            {eventList && Object.values(eventTable).map((option) => {
                                const event = eventList.find(e => e.eventId === option.index);
                                if (!event || !event.isActive) return null;
                                return (
                                    <Option key={option.index} value={option.index}>
                                        <>
                                            {eventTable[option.index]?.nameText || ''}({option.index}){' '}
                                            {eventTable[option.index]?.difficultSet === 1 && (
                                                <span style={{color: 'green', fontWeight: 'bold'}}> [Easy]</span>
                                            )}
                                            {eventTable[option.index]?.difficultSet === 2 && (
                                                <span style={{color: 'red', fontWeight: 'bold'}}> [Hard]</span>
                                            )}
                                            {rows.typeIndex !== option.index && (
                                                <span style={{
                                                    float: 'right',
                                                    color: '#888',
                                                    fontStyle: 'italic',
                                                    marginLeft: 8
                                                }}>
                                                    {event.memo}
                                                </span>
                                            )}
                                        </>
                                    </Option>
                                );
                            })}
                        </Select>
                    </Form.Item>
                    {segmentList && (
                        <Form.Item label="세그먼트 조건" style={{width: 240}}>
                            <Select
                                value={rows.segmentIdx || undefined}
                                onChange={value => handleSetRows('segmentIdx', value)}
                                placeholder="조건 선택"
                                options={segmentList.map(segment => ({
                                    value: segment.seq,
                                    label: `[${segment.seq}] ${segment.memo || ''}`,
                                }))}
                            />
                        </Form.Item>
                    )}
                    <Form.Item label="활성/비활성" style={{width: 100}}>
                        <Select
                            value={rows.onOff}
                            onChange={value => handleSetRows('onOff', value)}
                            size="middle"
                        >
                            {onOffList.map((option) => (
                                <Option key={option.value} value={option.value}>
                                    {option.label}
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>
                    <Form.Item label="계정 레벨" style={{width: 100}}>
                        <InputNumber
                            min={1}
                            value={rows.openLevel}
                            onChange={value => handleSetRows('openLevel', value)}
                            style={{width: '100%'}}
                        />
                    </Form.Item>
                    <Form.Item label="최대 레벨" style={{width: 100}}>
                        <InputNumber
                            min={1}
                            value={rows.maxLevel}
                            onChange={value => handleSetRows('maxLevel', value)}
                            style={{width: '100%'}}
                        />
                    </Form.Item>
                </div>
                <CustomDateTimePicker rows={rows} handleSetRows={handleSetRows} hasAdd={false}/>
                <DateStepper setRows={setRows} entity={EventEntity}/>
                {carnivalEventType > 0 && (
                    <div style={{
                        border: '1px dashed #faad14',
                        borderRadius: 8,
                        padding: 12,
                        marginBottom: 16,
                        background: '#fffbe6'
                    }}>
                        <b>{`종속 이벤트: ${eventTypeName(carnivalEventType)} (이 이벤트가 편성된 기간 안에서만 운영할 수 있습니다)`}</b>
                        {carnivalBaseEvents.length === 0 ? (
                            <Alert
                                type="warning"
                                showIcon
                                style={{marginTop: 8}}
                                message="종속 이벤트가 편성되어 있지 않습니다. 먼저 종속 이벤트를 등록하세요."
                            />
                        ) : (
                            <div style={{marginTop: 8}}>
                                {carnivalBaseEvents.map((row) => (
                                    <div key={row.seq} style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 12,
                                        padding: '4px 8px',
                                        background: '#fff',
                                        border: '1px solid #e8e8e8',
                                        borderRadius: 6,
                                        marginBottom: 6
                                    }}>
                                        <span style={{width: 70, color: '#888'}}>seq {row.seq}</span>
                                        <span style={{flex: 1}}>{eventTable[row.typeIndex]?.nameText || row.typeIndex}({row.typeIndex})</span>
                                        <span>{periodText(row)}</span>
                                        <Button
                                            size="small"
                                            type="primary"
                                            onClick={() => setRows(prevState => new EventEntity({
                                                ...prevState,
                                                begin: row.begin,
                                                end: row.end
                                            }))}
                                        >
                                            기간 적용
                                        </Button>
                                    </div>
                                ))}
                            </div>
                        )}
                        {carnivalOutOfRange && (
                            <Alert
                                type="error"
                                showIcon
                                style={{marginTop: 8}}
                                message="종속 이벤트 기간을 벗어났습니다"
                                description="위 목록 중 한 편성의 기간 안에 들어가도록 시작일과 종료일을 조정하세요."
                            />
                        )}
                        {carnivalOverlaps.length > 0 && (
                            <Alert
                                type="error"
                                showIcon
                                style={{marginTop: 8}}
                                message="다른 포인트 카니발과 기간이 겹칩니다"
                                description={`${carnivalOverlaps
                                    .map(row => `seq ${row.seq} / ${eventTypeName(eventTable[row.typeIndex]?.carnivalEventType)} / ${periodText(row)}`)
                                    .join(', ')} : 포인트 카니발은 참조 이벤트가 달라도 동시에 하나만 운영할 수 있습니다.`}
                            />
                        )}
                    </div>
                )}
                <div className={"param"} style={{display: 'flex', gap: 16}}>
                    <Form.Item label="운영 타입" style={{flex: 1, minWidth: 100}}>
                        <Select
                            value={rows.operationType}
                            onChange={value => handleSetRows('operationType', value)}
                            size="middle"
                        >
                            {operationTypeList.map((option) => (
                                <Option
                                    key={option.value}
                                    value={option.value}
                                    disabled={option.value === OPERATION_TYPE_TRIGGER && isSurprise !== 1}
                                >
                                    {option.label}
                                    {option.value === OPERATION_TYPE_TRIGGER && isSurprise !== 1 ? ' (깜짝 이벤트 전용)' : ''}
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>
                    {rows.operationType === 3 ? (
                        <Form.Item label="시작 날짜(요일)" style={{width: 200}}>
                            <Select
                                value={rows.onTime}
                                onChange={value => handleSetRows('onTime', value)}
                                size="middle"
                            >
                                {weekList.map((option) => (
                                    <Option key={option.value} value={option.value}>
                                        {option.label}
                                    </Option>
                                ))}
                            </Select>
                        </Form.Item>
                    ) : (
                        <Form.Item label="시작 날짜(일)" style={{width: 200}}>
                            <InputNumber
                                min={minArrived}
                                value={rows.onTime}
                                disabled={rows.operationType === 1 || rows.operationType === 4 || isSurprise}
                                onChange={value => {
                                    let v = value;
                                    if (v < minArrived) v = minArrived;
                                    handleSetRows('onTime', v);
                                }}
                                style={{width: '100%'}}
                            />
                        </Form.Item>
                    )}
                    <Form.Item label="시작 시간(UTC+9)" style={{width: 150}}>
                        <TimePicker
                            value={formatMinutes(rows.startTime)}
                            disabled={rows.operationType === 1 || isSurprise}
                            onChange={value => handleSetRows('startTime', getTotalMinutes(value))}
                            format="HH:mm"
                            style={{width: '100%'}}
                        />
                    </Form.Item>
                </div>
                {isTrigger && (
                    <div className={"param"}
                         style={{
                             border: '1px dashed #4096ff',
                             borderRadius: 8,
                             padding: 12,
                             marginBottom: 16,
                             background: '#fafcff'
                         }}>
                        <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            marginBottom: 12
                        }}>
                            <b>⚡ 선행 조건 복수 세팅 (AND = 단계 구분 / OR = 같은 단계)</b>
                            <Space>
                                {triggerOpList.map((option) => (
                                    <Button
                                        key={option.value}
                                        type="primary"
                                        size="small"
                                        onClick={() => addTrigger(option.value)}
                                        style={option.value === 1 ? {background: option.color, borderColor: option.color} : undefined}
                                    >
                                        + {option.value === 0 ? 'AND' : 'OR'} 조건 추가
                                    </Button>
                                ))}
                            </Space>
                        </div>
                        <div style={{marginBottom: 12}}>
                            <div style={{marginBottom: 6}}>발동 가능 요일</div>
                            <Radio.Group
                                value={weekdayPreset}
                                onChange={e => e.target.value !== 0 && handleSetRows('weekdays', e.target.value)}
                                optionType="button"
                                buttonStyle="solid"
                                size="small"
                                options={weekdayPresetList.map(preset => ({label: preset.label, value: preset.value}))}
                                style={{marginBottom: 6}}
                            />
                            <div>
                                {weekList.map(day => (
                                    <Checkbox
                                        key={day.value}
                                        checked={(rows.weekdays & (1 << day.value)) !== 0}
                                        onChange={e => setWeekday(day.value, e.target.checked)}
                                    >
                                        {day.label}
                                    </Checkbox>
                                ))}
                            </div>
                        </div>
                        {rows.triggers.length === 0 && (
                            <div style={{color: '#888'}}>선행 조건을 1개 이상 추가해야 저장할 수 있습니다.</div>
                        )}
                        {missingTriggerEvents.length > 0 && (
                            <Alert
                                type="error"
                                showIcon
                                style={{marginBottom: 12}}
                                message="해당되는 이벤트가 없습니다"
                                description={`${missingTriggerEvents.join(', ')} : 이 이벤트 기간(시작일~종료일) 안에 편성된 일정이 없습니다. 선행 이벤트를 먼저 등록하거나 기간을 조정하세요.`}
                            />
                        )}
                        {triggerSteps.length > 0 && (
                            <div style={{marginBottom: 12, padding: '6px 10px', background: '#fff', border: '1px solid #e8e8e8', borderRadius: 6}}>
                                <span style={{color: '#888', marginRight: 8}}>발동 순서</span>
                                {triggerSteps.map((step, i) => (
                                    <span key={i}>
                                        {i > 0 && <span style={{margin: '0 6px', color: '#888'}}>➡️</span>}
                                        <b>{`${i + 1}단계: `}</b>
                                        {step.map(triggerLabel).join(' 또는 ')}
                                    </span>
                                ))}
                            </div>
                        )}
                        {rows.triggers.map((trigger, i) => {
                            const op = triggerOpList[trigger.op] || triggerOpList[0];
                            return (
                                <React.Fragment key={i}>
                                    {i > 0 && (
                                        <div style={{textAlign: 'center', margin: '6px 0'}}>
                                            <Button
                                                size="small"
                                                shape="round"
                                                title="클릭하면 AND / OR 이 바뀝니다"
                                                onClick={() => setTrigger(i, {op: trigger.op === 1 ? 0 : 1})}
                                                style={{
                                                    color: op.color,
                                                    background: op.background,
                                                    borderColor: op.color,
                                                    fontWeight: 'bold'
                                                }}
                                            >
                                                {op.label}
                                            </Button>
                                        </div>
                                    )}
                                    <div style={{
                                        display: 'flex',
                                        gap: 16,
                                        alignItems: 'flex-end',
                                        background: '#fff',
                                        border: '1px solid #e8e8e8',
                                        borderRadius: 6,
                                        padding: '8px 12px'
                                    }}>
                                        <Form.Item label={`조건 ${i + 1} 유형`} style={{width: 180, marginBottom: 0}}>
                                            <Select
                                                value={trigger.type}
                                                onChange={value => setTrigger(i, {type: value, targetId: null, count: 1})}
                                            >
                                                {triggerTypeList.map((option) => (
                                                    <Option key={option.value} value={option.value}>{option.label}</Option>
                                                ))}
                                            </Select>
                                        </Form.Item>
                                        <Form.Item
                                            label={trigger.type === 1 ? '선행 대상 (이벤트 선택)' : `선행 대상 (인게임 액션 ${triggerActionList.length}종)`}
                                            style={{flex: 1, minWidth: 240, marginBottom: 0}}
                                        >
                                            <Select
                                                value={trigger.targetId}
                                                onChange={value => setTrigger(i, {targetId: value})}
                                                placeholder={trigger.type === 1 ? '이벤트 선택' : '액션 선택'}
                                                showSearch
                                                filterOption={(input, option) => String(option.children).toLowerCase().includes(input.toLowerCase())}
                                            >
                                                {trigger.type === 1
                                                    ? Object.values(eventTable)
                                                        .filter(option => option.index !== rows.typeIndex
                                                            && eventList.some(e => e.eventId === option.index && e.isActive))
                                                        .map((option) => (
                                                            <Option key={option.index}
                                                                    value={option.index}>{`${option.nameText || ''}(${option.index})`}</Option>
                                                        ))
                                                    : triggerActionList.map((option) => (
                                                        <Option key={option.value} value={option.value}>{option.label}</Option>
                                                    ))}
                                            </Select>
                                        </Form.Item>
                                        <Form.Item
                                            label={trigger.type === 2 && triggerActionList.find(a => a.value === trigger.targetId)?.isState ? '기준 보유량' : '트리거 발동 수치'}
                                            style={{width: 160, marginBottom: 0}}
                                        >
                                            <InputNumber
                                                min={1}
                                                value={trigger.count}
                                                onChange={value => setTrigger(i, {count: value ?? 1})}
                                                style={{width: '100%'}}
                                            />
                                        </Form.Item>
                                        <Form.Item style={{marginBottom: 0}}>
                                            <Button danger onClick={() => removeTrigger(i)}>삭제</Button>
                                        </Form.Item>
                                    </div>
                                </React.Fragment>
                            );
                        })}
                    </div>
                )}
                <div className={"param"} style={{display: 'flex', gap: 16, alignItems: 'flex-start'}}>
                    <Form.Item label={<span>{'운영기간(분)'}<span style={{fontSize: 12, color: '#888', fontWeight: 'normal'}}>= {Math.floor(rows.arrived / 1440)}일 {Math.floor((rows.arrived % 1440) / 60)}시간 {rows.arrived % 60}분</span></span>}>
                        <Space.Compact block>
                            <Button
                                disabled={rows.operationType === 1 || rows.arrived <= 1440}
                                onClick={() => handleSetRows('arrived', Math.max(minArrived, rows.arrived - 1440))}
                            >
                                -1일
                            </Button>
                            <Button
                                disabled={rows.operationType === 1 || rows.arrived <= 60}
                                onClick={() => handleSetRows('arrived', Math.max(minArrived, rows.arrived - 60))}
                            >
                                -1h
                            </Button>
                            <InputNumber
                                min={minArrived}
                                value={rows.arrived}
                                disabled={rows.operationType === 1 || isSurprise}
                                onChange={value => {
                                    let v = value;
                                    if (v < minArrived) v = minArrived;
                                    handleSetRows('arrived', v);
                                }}
                                style={{width: 100}}
                            />
                            <Button
                                disabled={rows.operationType === 1 || isSurprise}
                                onClick={() => handleSetRows('arrived', rows.arrived + 60)}
                            >
                                +1h
                            </Button>
                            <Button
                                disabled={rows.operationType === 1 || isSurprise}
                                onClick={() => handleSetRows('arrived', rows.arrived + 1440)}
                            >
                                +1일
                            </Button>
                        </Space.Compact>
                        <div style={{marginTop: 6}}>
                            <Space size={4} wrap>
                                {[1, 3, 7, 14, 30].map(day => {
                                    const minutes = day * 1440;
                                    return (
                                        <Button
                                            key={day}
                                            size="small"
                                            type={rows.arrived === minutes ? 'primary' : 'default'}
                                            disabled={rows.operationType === 1 || isSurprise}
                                            onClick={() => handleSetRows('arrived', minutes)}
                                        >
                                            {day}일
                                        </Button>
                                    );
                                })}
                            </Space>
                        </div>
                    </Form.Item>
                    <Form.Item label="재발동 쿨타임(분)" style={{width: 150}}>
                        <InputNumber
                            min={0}
                            value={rows.cooldownMinutes}
                            onChange={value => handleSetRows('cooldownMinutes', value ?? 0)}
                            style={{width: '100%'}}
                        />
                    </Form.Item>
                    <Form.Item label="일일 최대 발동 횟수" style={{width: 150}}>
                        <InputNumber
                            min={0}
                            value={rows.dailyMaxCount}
                            onChange={value => handleSetRows('dailyMaxCount', value ?? 0)}
                            style={{width: '100%'}}
                        />
                    </Form.Item>
                </div>
                {isSurprise === 1 && (
                    <Form.Item label="깜짝 이벤트 운영 시간(분)" style={{width: 300}}>
                        <InputNumber
                            min={1}
                            value={rows.surpriseTime}
                            onChange={value => handleSetRows('surpriseTime', value)}
                            style={{width: '100%'}}
                        />
                    </Form.Item>
                )}
                <div style={{ display: 'flex', gap: 16 }}>
                    <Form.Item label="Memo" style={{width: 400}}>
                        <Input
                            value={rows.memo}
                            onChange={e => handleSetRows('memo', e.target.value)}
                            style={{width: '100%'}}
                        />
                    </Form.Item>
                    <Form.Item label="최소 적용 버전" style={{width: 400}}>
                        <Input
                            value={eventTable[rows.typeIndex].openVersion || ''}
                            style={{width: '100%'}}
                            disabled={true}
                        />
                    </Form.Item>
                </div>
                <div className={"event-edit-buttons"}>
                    <SaveButton onClick={handleCheck} handleSetDialog={handleSetDialog}
                                disabled={missingTriggerEvents.length > 0 || carnivalOutOfRange || carnivalOverlaps.length > 0
                                    || (carnivalEventType > 0 && carnivalBaseEvents.length === 0)}/>
                    <CancelButton onClick={handleClose}/>
                    <DeleteButton onClick={handleDelete} display={rows.seq > 0 ? 'flex' : 'none'}
                                  handleSetDialog={handleSetDialog}/>
                </div>
            </Form>
        </div>
    );
}

export default EventEditModal;
